import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  changeOrdersTab,
  changeOrdersType,
  getOldestInWarehouseOrderFulfilled,
  getOrderByIdFulfilled,
  getOrderByIdPending,
  getOrderByIdRejected,
  getOrdersFulfilled,
  getOrdersPending,
  getOrdersRejected,
  getOrdersStatesSummaryFulfilled,
  getOrdersStatesSummaryPending,
  getOrdersStatesSummaryRejected,
  onChangeOrderInState,
  onChangeOrderSelection,
  onChangeOrderView,
  onFilterChange,
  onFlushOrderData,
  onPaginationChange,
  onSelectAll,
  onSetIsLoading,
  OrdersState,
} from "../reducers/orders-reducers";
import ordersService from "../../services/orders.service";
import { OrderModel } from "../../pages/shared/models/order/order-item.model";
import { OrderViewEnum } from "../../pages/orders/enums/order/view.enum";

export const getOrders = createAsyncThunk(
  "getOrders",
  async (props: { page: number; isAdmin?: boolean }, thunkApi) => {
    const ordersReducer: OrdersState = (thunkApi.getState() as any)[
      "ordersReducer"
    ];

    const filter = props.isAdmin
      ? {}
      : {
          type: ordersReducer.typeId,
          state: ordersReducer.orderStateId,
          ...ordersReducer.filter,
        };

    return await ordersService.getMyOrders(
      props.page,
      filter,
      ordersReducer.ordersData.pageSize,
    );
  },
);

export const getOrderById = createAsyncThunk(
  "getOrderById",
  async (id: number | undefined) => {
    return id ? await ordersService.getOrderById(id) : null;
  },
);

export const updateExistingOrder = createAsyncThunk(
  "updateExistingOrder",
  async (props: { updateData: { [key: string]: any }; languageId: number }) => {
    const orderId = props.updateData.orderId;
    delete props.updateData.orderId;

    if (props.updateData.products) {
      props.updateData.products.forEach((product: { [key: string]: any }) => {
        Object.keys(product).forEach((key) => {
          if (product[key] === undefined) {
            product[key] = null;
          }
        });
      });
    }

    return await ordersService.updateById(
      props.updateData,
      orderId,
      props.languageId,
    );
  },
);

export const updateExistingOrderState = createAsyncThunk(
  "updateExistingOrderState",
  async (updateData: { [key: string]: any }) => {
    return await ordersService.updateStateById(updateData);
  },
);

export const getOrdersStatesSummary = createAsyncThunk(
  "getOrdersStatesSummary",
  async () => {
    const summary = await ordersService.getMyOrdersStatesSummary();
    return summary?.data;
  },
);

export const getOldestOrderDateByStateId = createAsyncThunk(
  "getOldestOrderDateByStateId",
  async (stateId: number) => {
    const response = await ordersService.getOldestDateByState(stateId || 1);
    const oldestDate =
      response && response.data && response.data.oldestEnteredDate;

    return oldestDate || "";
  },
);

export const ordersSlice = createSlice({
  name: "orders",
  initialState: {
    typeId: 1,
    orderStateId: 1,
    tabId: 3, // FOR ADMIN USERS
    filter: {},
    ordersData: {
      currentPage: 0,
      orders: [] as OrderModel[],
      pageSize: 8,
      totalItems: 0,
    },
    allOrdersData: {
      orders: [] as OrderModel[],
      totalItems: 0,
    },
    ordersStateSummary: {} as { [key: number | string]: number },
    oldestInWarehouseOrderDate: "",
    orderViewType: OrderViewEnum.list,
    isLoading: false,
    summaryIsLoading: false,

    hasAllSelected: false,
  } as OrdersState,
  reducers: {
    changeTab: changeOrdersTab,
    changeState: changeOrdersType,
    changeFilter: onFilterChange,
    changePagination: onPaginationChange,
    changeOrderInState: onChangeOrderInState,
    changeOrderSelection: onChangeOrderSelection,
    changeOrderView: onChangeOrderView,
    selectAll: onSelectAll,

    setIsLoading: (state, action) => onSetIsLoading(state, action.payload),
    flushOrderData: onFlushOrderData,
  },
  extraReducers: (builder) => {
    builder.addCase(getOrders.pending, (state) =>
      getOrdersPending(state, true),
    );
    builder.addCase(getOrders.fulfilled, (state, action) =>
      getOrdersFulfilled(state, action.payload),
    );
    builder.addCase(getOrders.rejected, (state) => getOrdersRejected(state));
    builder.addCase(getOrdersStatesSummary.pending, (state) =>
      getOrdersStatesSummaryPending(state, true),
    );
    builder.addCase(getOrdersStatesSummary.fulfilled, (state, action) =>
      getOrdersStatesSummaryFulfilled(state, action.payload),
    );
    builder.addCase(getOrdersStatesSummary.rejected, (state) =>
      getOrdersStatesSummaryRejected(state),
    );
    builder.addCase(getOldestOrderDateByStateId.fulfilled, (state, action) =>
      getOldestInWarehouseOrderFulfilled(state, action.payload),
    );
    builder.addCase(getOrderById.pending, (state) =>
      getOrderByIdPending(state),
    );
    builder.addCase(getOrderById.fulfilled, (state) =>
      getOrderByIdFulfilled(state),
    );
    builder.addCase(getOrderById.rejected, (state) =>
      getOrderByIdRejected(state),
    );
  },
});

export default ordersSlice.reducer;
export const {
  changeTab,
  changeState,
  changeFilter,
  changePagination,
  changeOrderInState,
  changeOrderSelection,
  changeOrderView,
  selectAll,
  flushOrderData,
  setIsLoading,
} = ordersSlice.actions;
